import { useLazyQuery } from '@apollo/client'
import { APIStatus } from '@flowwai-frontend/shared/api-common'

import CustomError from '../../models/CustomError'
import {
   convertToErrorType,
   getAPIStatusFromNetworkStatus
} from '../../utils/APIUtils'
import {
   GetPipelinesForPipelineItemsDocument as QueryDocument,
   GetPipelinesForPipelineItemsQuery as Query,
   GetPipelinesForPipelineItemsQueryVariables as QueryVariables
} from './query.generatedTypes'
import useAPIResponseHandler from './responseHandler'

export interface FailureArgsType {
   error?: Error | null
   data?: CustomError<Query['getPipelinesForPipelineItems']>
}

interface TriggerAPIArgsType {
   pipelineItemIds: string[]

   onFailure?: (args: FailureArgsType) => void
   onSuccess?: (pipelineIds: Array<string>) => void
}

interface ReturnType {
   apiError: Error | null
   apiStatus: APIStatus

   triggerAPI: (args: TriggerAPIArgsType) => Promise<void>
}

const useGetPipelinesForPipelineItem = (): ReturnType => {
   const [
      getPipelinesForPipelineItems,
      { error, networkStatus, data }
   ] = useLazyQuery<Query, QueryVariables>(QueryDocument)

   const { handleAPIResponse } = useAPIResponseHandler()

   const triggerAPI = async (args: TriggerAPIArgsType): Promise<void> => {
      const requestObj: QueryVariables = {
         params: {
            pipelineItemIds: args.pipelineItemIds
         }
      }

      const response = await getPipelinesForPipelineItems({
         variables: requestObj,

         onError(error) {
            args.onFailure?.({ error })
         }
      })
      if (!response.data) return

      handleAPIResponse({
         data: response.data,

         onHandleError: (
            errorMessage: string,
            data: Query['getPipelinesForPipelineItems']
         ) => args.onFailure?.({ data: new CustomError(errorMessage, data) }),
         onSuccess: args.onSuccess
      })
   }

   const apiError = convertToErrorType(error)
   const apiStatus = getAPIStatusFromNetworkStatus(networkStatus, data)

   return {
      apiError,
      apiStatus,

      triggerAPI
   }
}

export default useGetPipelinesForPipelineItem
